import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as apiMethods from '../res/apiMethods.js';
import Sidebar from '../components/Sidebar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const OP_COLORS = {
  PAGO_PROCESADO:     '#2e7d32',
  LIQUIDACION:        '#1565c0',
  ACTUALIZAR_CREDITO: '#e65100',
  CREAR_CREDITO:      '#6a1b9a',
  ELIMINAR_CREDITO:   '#b71c1c',
  CIERRE_PERIODO:     '#37474f',
};
const opColor = (op) => OP_COLORS[op] || '#546e7a';

const formatTs = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('es-CO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return ts; }
};

const LogRow = ({ entry, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff', borderBottom: '1px solid #ececec' }}>
        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#555', whiteSpace: 'nowrap' }}>
          {formatTs(entry.timestamp)}
        </td>
        <td style={{ padding: '10px 12px' }}>
          <Chip
            label={entry.operation || '—'}
            size="small"
            sx={{ backgroundColor: opColor(entry.operation), color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
          />
        </td>
        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#555' }}>
          {entry.username || entry.userId || '—'}
        </td>
        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#555' }}>
          {entry.creditoId || '—'}
        </td>
        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
          <IconButton size="small" onClick={() => setOpen(!open)} sx={{ color: '#cb1016' }}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </td>
      </tr>
      <tr>
        <td colSpan={5} style={{ padding: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <div style={{ margin: '4px 12px 8px', padding: '12px 16px', backgroundColor: '#f4f4f4', borderRadius: 4, overflowX: 'auto' }}>
              <pre style={{ margin: 0, fontSize: '12px', color: '#333', fontFamily: '"Consolas", monospace', lineHeight: 1.6 }}>
                {JSON.stringify(entry, null, 2)}
              </pre>
            </div>
          </Collapse>
        </td>
      </tr>
    </>
  );
};

const Logs = () => {
  const [lines, setLines] = useState(200);
  const [operacion, setOperacion] = useState('');
  const [creditoId, setCreditoId] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(null);
  const [operaciones, setOperaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const token = window.sessionStorage.getItem('localToken');
    const username = window.sessionStorage.getItem('username');
    if (!token) { window.location.href = '/login'; return; }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!(payload.roleId === 1 && username === 'complemento360')) {
        window.location.href = '/accessDenied';
        return;
      }
    } catch {
      window.location.href = '/accessDenied';
      return;
    }
    (async () => {
      try {
        const res = await axios.get(apiMethods.GET_LOGS_OPERACIONES, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.code === 0) setOperaciones(res.data.operaciones || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchLogs = async () => {
    const token = window.sessionStorage.getItem('localToken');
    setLoading(true);
    try {
      const params = {};
      if (lines !== '' && lines !== null) params.lines = Number(lines);
      if (operacion) params.operacion = operacion.toUpperCase();
      if (creditoId.trim()) params.creditoId = creditoId.trim();
      const res = await axios.get(apiMethods.GET_LOGS, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.code === 0) {
        setEntries(res.data.entries || []);
        setTotal(res.data.total ?? (res.data.entries || []).length);
      } else {
        setEntries([]);
        setTotal(0);
      }
    } catch {
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const downloadLogs = async () => {
    const token = window.sessionStorage.getItem('localToken');
    try {
      const res = await axios.get(apiMethods.GET_LOGS_DOWNLOAD, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'logs.txt';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') fetchLogs(); };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '15% 85%' }}>
      <Sidebar />

      <div className="card" style={{ padding: '2rem' }}>
        {/* Título */}
        <div style={{ fontSize: '15pt', fontWeight: '500', marginBottom: '2rem', marginTop: '1rem', marginLeft: '1rem' }}>
          Logs del sistema
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginLeft: '1rem', marginBottom: '1.5rem' }}>
          <TextField
            label="Líneas (0 = todas)"
            type="number"
            size="small"
            value={lines}
            onChange={e => setLines(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ width: 160 }}
            inputProps={{ min: 0 }}
          />

          <FormControl size="small" style={{ minWidth: 220 }}>
            <InputLabel>Operación</InputLabel>
            <Select
              value={operacion}
              label="Operación"
              onChange={e => setOperacion(e.target.value)}
            >
              <MenuItem value=""><em>-- Todas --</em></MenuItem>
              {operaciones.map(op => (
                <MenuItem key={op} value={op}>{op}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Crédito Id"
            size="small"
            value={creditoId}
            onChange={e => setCreditoId(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ width: 160 }}
          />

          <Button
            variant="contained"
            onClick={fetchLogs}
            disabled={loading}
            sx={{
              backgroundColor: 'rgb(238,238,238)',
              color: 'black',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#e92128', color: 'white', fontWeight: 'bold', boxShadow: 'none' },
            }}
          >
            {loading ? 'Cargando…' : 'Listar'}
          </Button>

          <Button
            variant="contained"
            onClick={downloadLogs}
            sx={{
              backgroundColor: 'rgb(238,238,238)',
              color: 'black',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#e92128', color: 'white', fontWeight: 'bold', boxShadow: 'none' },
            }}
          >
            Descargar archivo
          </Button>
        </div>

        {/* Resultados */}
        {searched && (
          <>
            <div style={{ marginLeft: '1rem', marginBottom: '0.75rem', fontSize: '13px', color: '#555' }}>
              <strong>Total: </strong>{total ?? entries.length}
            </div>

            {entries.length === 0 ? (
              <div style={{ marginLeft: '1rem', color: '#999', fontSize: '13px' }}>No se encontraron registros.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ececec', backgroundColor: '#fafafa' }}>
                      {['Timestamp', 'Operación', 'Usuario', 'Crédito Id', ''].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#444' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, i) => (
                      <LogRow key={i} entry={entry} index={i} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Logs;
